import { CheckCircle2, FileUp, Loader2, X, XCircle } from "lucide-react";

import {
  useBookmarkImportProgressActions,
  useBookmarkImportProgressStore,
  type BookmarkImportPhase,
} from "@/modules/mine/bookmark-import";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";

function formatCount(value?: string) {
  if (!value) {
    return "0";
  }

  return value;
}

function formatProcessedCount(processedCount?: string, totalCount?: string) {
  if (!totalCount) {
    return "0 / 0";
  }

  return `${formatCount(processedCount)} / ${formatCount(totalCount)}`;
}

function formatImportSummary(task: ReturnType<typeof useBookmarkImportProgressStore.getState>["task"]) {
  return `已处理 ${formatProcessedCount(task?.processed_count, task?.total_count)}，导入 ${formatCount(task?.imported_folder_count)} 个文件夹、${formatCount(task?.imported_bookmark_count)} 个书签`;
}

function getStatusLabel(phase: BookmarkImportPhase) {
  if (phase === "creating") {
    return "正在创建导入任务";
  }

  if (phase === "uploading") {
    return "正在上传书签文件";
  }

  if (phase === "confirming") {
    return "正在确认上传完成";
  }

  if (phase === "processing") {
    return "正在导入书签";
  }

  if (phase === "succeeded") {
    return "导入完成";
  }

  if (phase === "failed") {
    return "导入失败";
  }

  return "等待导入";
}

function getPhaseIcon(phase: BookmarkImportPhase) {
  if (phase === "succeeded") {
    return <CheckCircle2 className="size-4 text-green-600" />;
  }

  if (phase === "failed") {
    return <XCircle className="size-4 text-destructive" />;
  }

  if (phase === "idle") {
    return <FileUp className="size-4 text-muted-foreground" />;
  }

  return <Loader2 className="size-4 animate-spin text-muted-foreground" />;
}

export function BookmarkImportProgressPanel() {
  const visible = useBookmarkImportProgressStore((state) => state.visible);
  const fileName = useBookmarkImportProgressStore((state) => state.fileName);
  const phase = useBookmarkImportProgressStore((state) => state.phase);
  const progress = useBookmarkImportProgressStore((state) => state.progress);
  const task = useBookmarkImportProgressStore((state) => state.task);
  const errorMessage = useBookmarkImportProgressStore((state) => state.errorMessage);
  const { dismiss } = useBookmarkImportProgressActions();

  if (!visible) {
    return null;
  }

  const roundedProgress = Math.round(progress);

  return (
    <div className="fixed top-20 right-5 z-[90] w-[460px] max-w-[calc(100vw-2.5rem)] rounded-lg border bg-popover p-4 text-popover-foreground shadow-md">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 size-7"
        onClick={dismiss}
      >
        <X className="size-3.5" />
        <span className="sr-only">关闭</span>
      </Button>
      <div className="min-w-0">
        <div className="flex items-center justify-start gap-4">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted">
              {getPhaseIcon(phase)}
            </span>
            <span className="truncate text-sm font-medium">{getStatusLabel(phase)}</span>
          </div>
        </div>

        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-300",
              phase === "failed" ? "bg-destructive" : "bg-emerald-400/80",
            )}
            style={{ width: `${roundedProgress}%` }}
          />
        </div>

        <div className="mt-2 truncate text-xs text-muted-foreground flex items-center justify-between">
          <span>{fileName}</span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {roundedProgress}%
          </span>
        </div>

        {phase === "failed" ? (
          <div className="mt-2 text-xs text-destructive">
            {errorMessage || "书签导入失败，请稍后重试。"}
          </div>
        ) : (
          <div className="mt-1.5 text-xs text-muted-foreground">
            {formatImportSummary(task)}
          </div>
        )}
      </div>
    </div>
  );
}
